import { Box, CardContent, Grid, Stack } from '@mui/material';
import { CorePermissions } from 'app/appPermissions';
import { MprcBarTitle, MprcCard, MprcChipChoice, MprcContainer, MprcFormSubHeader, MprcInfiniteLoading, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { ButtonSave } from 'components/buttons';
import { MprcCardFilterBox } from 'components/filter/MprcCardFilterBox';
import { ExecuteUserOperationOptions, UserInfoDtoPaginatedList, UserOperationSettingsResponse, UserOperationType, UserStatus } from 'generated/CoreApi_v1';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { UserAdditionalInfoOperation, UserBlockOperation, UserGeneralInfoOperation, UserGroupsOperation, UserManagementOperation, UserRolesOperation, UserTagsOperation, UserTopicsOperation, UserUnitsOperation } from 'views/foundation/user/Home/BatchEdit/OperationBoxOption';
import { OperationResultsDialog } from 'views/foundation/user/Home/BatchEdit/OperationResultsDialog';
import { OperationSearchUsers } from 'views/foundation/user/Home/BatchEdit/OperationSearchUsers';
import { OperationSelectedUsers } from 'views/foundation/user/Home/BatchEdit/OperationSelectedUsers';
import { EditUserHeader } from 'views/foundation/user/Home/EditUser/EditUserHeader';


export type UserOperation = {
  value: UserOperationType;
  text: string;
  disabled: boolean;
};

export interface UserBatchEditContextType {
  operation?: UserOperationType;
  setOperation: React.Dispatch<React.SetStateAction<UserOperationType | undefined>>;
  options: ExecuteUserOperationOptions;
  setOptions: React.Dispatch<React.SetStateAction<Partial<ExecuteUserOperationOptions>>>;
  selectedUsers?: UserInfoDtoPaginatedList;
  setSelectedUsers: React.Dispatch<React.SetStateAction<UserInfoDtoPaginatedList | undefined>>;
  reloadSelectedUsers: boolean;
  setReloadSelectedUsers: React.Dispatch<React.SetStateAction<boolean>>;
  reloadSearchUsers: boolean;
  setReloadSearchUsers: React.Dispatch<React.SetStateAction<boolean>>;
  settings: UserOperationSettingsResponse;
  isImportedFile: boolean;
  setIsImportedFile: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserBatchEditContext = React.createContext<UserBatchEditContextType>(null!);
export const useUserBatchEditContext = () => React.useContext(UserBatchEditContext);


export const UserBatchEdit: React.FC = () => {

  const api = useCoreApi();
  const { t } = useTranslation("");
  const navigate = useNavigate();
  const session = useSession();

  const [operation, setOperation] = useState<UserOperationType | undefined>(UserOperationType.Block);
  const [dialog, setDialog] = useState<any>();
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [isImportedFile, setIsImportedFile] = useState<boolean>(false);
  const [reloadSelectedUsers, setReloadSelectedUsers] = useState<boolean>(false);
  const [reloadSearchUsers, setReloadSearchUsers] = useState<boolean>(false);
  const [options, setOptions] = useState<ExecuteUserOperationOptions>({} as ExecuteUserOperationOptions);
  const [selectedUsers, setSelectedUsers] = useState<UserInfoDtoPaginatedList | undefined>();
  const [settings, setSettings] = useState<UserOperationSettingsResponse>({
    customFields: [],
    isWorkplaceFreeText: true,
    locale: "",
    workplaceLabel: "",
    secondaryManagers: [],
    blockReasons: [],
    unitTypes: []
  });

  const canSave = useMemo(() => {

    if ((selectedUsers?.items ?? []).length == 0) return false;
    if (operation == undefined) return false;

    var isValid = false;

    switch (operation) {
      case UserOperationType.Block:
        isValid = options.block?.status == UserStatus.Active || options.block?.blockReasonId != null;
        break;

      case UserOperationType.Tags:
        isValid = options.tag?.tagId != undefined && options.tag.action != undefined;
        break;

      case UserOperationType.Groups:
        isValid = options.group?.groupId != undefined && options.group.action != undefined;
        break;

      case UserOperationType.Units:
        isValid = options.unit?.unitId != undefined && options.unit?.action != undefined;
        break;

      case UserOperationType.Topics:
        isValid = options.topic?.topicId != undefined && options.topic?.action != undefined;
        break;

      case UserOperationType.Roles:
        isValid = options.role?.roleId != undefined && options.role?.roleId != undefined;
        break;

      case UserOperationType.GeneralInfo:
        isValid = options.generalInfo != null && Object.values(options.generalInfo).some(p => p != null);
        break;

      case UserOperationType.AdditionalInfo:
        isValid = options.additionalInfo?.customField?.fieldValue != null;
        break;

      case UserOperationType.Managers:
        isValid = options.management != null
          && (options.management.managerId != null
            || (options.management.secondaryManager?.managerId != null && options.management.secondaryManager?.managerTypeId != null));
        break;
    }

    return isValid;

  }, [operation, options, selectedUsers]);

  const userOperations = useMemo((): UserOperation[] => {
    var items: UserOperation[] =
      [
        { value: UserOperationType.Block, text: t("User.BatchEdit.Operation.BlockText"), disabled: false },
        { value: UserOperationType.GeneralInfo, text: t("User.BatchEdit.Operation.GeneralInfoText"), disabled: false }
      ];

    if (settings.customFields.length > 0) {
      items.push({ value: UserOperationType.AdditionalInfo, text: t("User.BatchEdit.Operation.AdditionalInfoText"), disabled: false });
    }

    items.push(
      { value: UserOperationType.Managers, text: t("User.BatchEdit.Operation.ManagersText"), disabled: true },
      { value: UserOperationType.Groups, text: t("User.BatchEdit.Operation.GroupsText"), disabled: true },
      { value: UserOperationType.Units, text: t("User.BatchEdit.Operation.UnitsText"), disabled: true },
      { value: UserOperationType.Topics, text: t("User.BatchEdit.Operation.TopicsText"), disabled: true },
      { value: UserOperationType.Tags, text: t("User.BatchEdit.Operation.TagsText"), disabled: true }
    );

    if (session.permissionExplorer.hasOne([CorePermissions.UsersAssignRole])) {
      items.push({ value: UserOperationType.Roles, text: t("User.BatchEdit.Operation.RolesText"), disabled: true });
    }

    return items;
  }, [settings]);

  const save = () => {
    if (canSave) {
      setSaving(true);

      api.v1.userOperation.executeUserOperation({
        operation: operation!,
        options: options,
        userIds: (selectedUsers?.items ?? []).map(item => item.userId)
      }).then(response => {
        if (response.data) {
          setDialog(
            <OperationResultsDialog
              results={response.data}
              onClose={() => {
                setDialog(null);
                setReloadSelectedUsers(true);
                setReloadSearchUsers(true);
              }}
            />
          );
        }
        else {
          enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
        }

      })
        .catch(e => {
          setDialog(
            <MprcRequestErrorDialog error={e} onClose={() => setDialog(null)} />
          );
        })
        .finally(() => setSaving(false));
    }
  };

  useEffect(() => {
    setOptions({} as ExecuteUserOperationOptions);
    if (operation != undefined) {
      setReloadSelectedUsers(true);
    }
  }, [operation]);

  useEffect(() => {
    setLoading(true);
    api.v1.userOperation.getSettings({ operation: null })
      .then(response => setSettings(response.data))
      .catch(e => setError(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <UserBatchEditContext.Provider
      value={
        {
          operation,
          setOperation,
          options,
          setOptions: newOptions => setOptions(state => ({ ...state, ...newOptions })),
          selectedUsers,
          setSelectedUsers,
          reloadSearchUsers,
          setReloadSearchUsers,
          reloadSelectedUsers,
          setReloadSelectedUsers,
          settings,
          isImportedFile,
          setIsImportedFile
        }
      }
    >
      {dialog}

      <EditUserHeader
        onClose={() => navigate(-1)}
        recordChanged={false}
        reloadOnClose={false}
        actions={
          [
            <MprcBarTitle>{t("User.BatchEdit.ViewTitle")}</MprcBarTitle>,
            <ButtonSave
              text={t("User.BatchEdit.ExecuteButtonText")}
              variant="text"
              disabled={!canSave}
              onClick={save}
            />
          ]}
      />
      {error
        ? <MprcRequestErrorReport error={error} />
        : loading
          ? <MprcInfiniteLoading visible />
          :
          <>
            <MprcCard>
              <MprcRequestIndicator visible={saving} type={"post"} />
              <MprcCardFilterBox>
                <MprcFormSubHeader
                  margin="normal"
                  title={t("User.BatchEdit.ChooseOperationLabel")}
                />
                <Stack direction="row" justifyContent="flex-start" alignContent="flex-start" flexWrap="wrap" gap={0.5}>
                  {
                    userOperations.map(x =>
                      <MprcChipChoice
                        label={x.text.toUpperCase()}
                        key={x.value}
                        selected={x.value == operation}
                        onClick={() => setOperation(x.value)}
                      />
                    )
                  }
                </Stack>
              </MprcCardFilterBox>
              <CardContent>
                <Stack direction="column" spacing={1}>
                  {operation == UserOperationType.Tags && <UserTagsOperation />}
                  {operation == UserOperationType.Groups && <UserGroupsOperation />}
                  {operation == UserOperationType.Roles && <UserRolesOperation />}
                  {operation == UserOperationType.Units && <UserUnitsOperation />}
                  {operation == UserOperationType.Block && <UserBlockOperation />}
                  {operation == UserOperationType.Topics && <UserTopicsOperation />}
                  {operation == UserOperationType.GeneralInfo && <UserGeneralInfoOperation />}
                  {operation == UserOperationType.AdditionalInfo && <UserAdditionalInfoOperation />}
                  {operation == UserOperationType.Managers && <UserManagementOperation />}
                </Stack>
              </CardContent>
            </MprcCard>
            <Box marginTop={2}>
              <MprcContainer>
                <Grid container spacing={3} wrap="nowrap">
                  <Grid size={{ xs: 8 }}>
                    {
                      operation &&
                      <OperationSearchUsers />
                    }
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    {
                      operation &&
                      <OperationSelectedUsers />
                    }
                  </Grid>
                </Grid>
              </MprcContainer>
            </Box>
          </>
      }
    </UserBatchEditContext.Provider>
  );
};
